"use client"

import type React from "react"
import { useState } from "react"
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from "reactstrap"
import { FaUsers, FaCar, FaBuilding } from "react-icons/fa"
import type { Staff, Car, TokenData } from "./types" // Added .ts extension
import StaffTab from "./StaffTab"
import CarTab from "./CarTab"
import "./StaffCarManagementList.css" 

interface Props {
  token: string
  tokenData: TokenData
  onAddStaff: () => void
  onEditStaff: (item: Staff) => void
  onAddCar: () => void
  onEditCar: (item: Car) => void
  refreshTrigger: boolean
}

const StaffCarManagementList: React.FC<Props> = ({
  token,
  tokenData,
  onAddStaff,
  onEditStaff,
  onAddCar,
  onEditCar,
  refreshTrigger,
}) => {
  const [activeTab, setActiveTab] = useState("1")

  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  return (
    <div>
      <style>
        {`
        .professional-header {
        background: linear-gradient(135deg, #e67118ff 0%, #065737ff 100%);
        border: none;
        color: white;
      }
 
.professional-card {
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
 
.professional-tabs .nav-link {
  border: none;
  border-radius: 8px 8px 0 0;
  padding: 12px 24px;
  font-weight: 500;
  color: #64748b;
  background: #f8fafc;
  margin-right: 4px;
  transition: all 0.3s ease;
}
 
.professional-tabs .nav-link:hover {
  background: #e2e8f0;
  color: #475569;
  transform: translateY(-1px);
}
 
.professional-tabs .nav-link.active {
  background: white;
  color: #1e293b;
  font-weight: 600;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.stats-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 0.75rem;
  padding: 4px 12px;
}
 
.limit-indicator {
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 12px;
  color: #1e293b;
  border-radius: 6px;
  font-size: 1rem;
}
 
.company-info {
  background: rgba(255, 255, 255, 0.1);
  padding: 16px;
  color: #1e293b;
  font-size: 1rem;
  border-radius: 8px;
  margin-top: 12px;
}
        `}
        </style>

      {/* The style tag is removed, as styles are now in a separate CSS file */}
      <Card className="mb-4 professional-card">
        <CardHeader className="professional-header">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-start">
              <div className="me-3 mt-1 text-dark">
                <FaBuilding size={28} style={{ opacity: 0.9 }} />
              </div>
              <div>
                <h3 className="mb-2 fw-bold " >{tokenData.company_name || "Exhibition Management"}</h3>
                {tokenData.exhibition_title && (
                  <p className="mb-3 fs-4 fw-bold" style={{ color: "#b45309" }}>
                    <strong>{tokenData.exhibition_title}</strong>
                  </p>
                )}
                <div className="d-flex flex-wrap gap-3 mb-2">
                  <div className="limit-indicator">
                    <FaUsers className="me-2" size={14} />
                    <strong>{tokenData.max_staff}</strong> Allowed Staff
                  </div>
                  <div className="limit-indicator">
                    <FaCar className="me-2" size={14} />
                    <strong>{tokenData.max_cars}</strong> Vehicle Slots
                  </div>
                </div>
                <div className="company-info fw-bold ">
                  <small className="d-block mb-1">
                    <strong>Contact:</strong> {tokenData.email}
                  </small>
                  <small className="d-block">
                    <strong>Phone:</strong> {tokenData.phone}
                  </small>
                </div>
              </div>
            </div>
            <Badge className="stats-badge">Active Registration</Badge>
          </div>
        </CardHeader>
      </Card>

      <Card className="professional-card">
        <CardHeader className="bg-light border-0 pb-0">
          <Nav tabs className="professional-tabs border-0">
            <NavItem>
              <NavLink
                className={activeTab === "1" ? "active" : ""}
                onClick={() => toggle("1")}
                style={{ cursor: "pointer" }}
              >
                <FaUsers className="me-2" size={16} />
                Staff Management
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "2" ? "active" : ""}
                onClick={() => toggle("2")}
                style={{ cursor: "pointer" }}
              >
                <FaCar className="me-2" size={16} />
                Vehicle Management
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody className="p-4">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <StaffTab
                token={token}
                tokenData={tokenData}
                onAdd={onAddStaff}
                onEdit={onEditStaff}
                refresh={refreshTrigger}
              />
            </TabPane>
            <TabPane tabId="2">
              <CarTab
                token={token}
                tokenData={tokenData}
                onAdd={onAddCar}
                onEdit={onEditCar}
                refresh={refreshTrigger}
              />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  )
}

export default StaffCarManagementList